// src/lib/stores/toast.svelte.ts
//
// Round 7 — minimal reactive toast queue. Used by the background
// notification routing to surface agent-finished events, workspace
// switches, and any other one-shot feedback the UI needs to
// communicate. Integrates with platform.notify() so Tauri desktop
// builds also pop a native OS notification.
//
// Usage:
//
//   import { toast } from './stores/toast.svelte.ts';
//   toast.push({ title: 'Agent finished', body: 'LOC-17 build succeeded', kind: 'success' });
//
// The <Toaster /> component (mounted once in App.svelte) reads this
// store and renders a bottom-right stack that auto-dismisses after
// 4s.

import { platform } from '../platform.ts';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id:          string;
  title:       string;
  body?:       string;
  kind:        ToastKind;
  /** ms until auto-dismiss. Default 4000. Pass 0 for sticky. */
  durationMs:  number;
  /** Optional click handler — used for "jump to workspace" etc. */
  onClick?:    () => void;
  /** ms epoch — when this toast was pushed. */
  createdAt:   number;
}

export const toastStore = $state({
  toasts: [] as Toast[],
});

function genId(): string {
  // Don't need crypto-strong — just unique within the session.
  return 't_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Push a toast. Returns the toast id so callers can dismiss early. */
function push(
  t: Omit<Toast, 'id' | 'createdAt' | 'durationMs'> & { durationMs?: number },
): string {
  const toast: Toast = {
    id:         genId(),
    title:      t.title,
    body:       t.body,
    kind:       t.kind,
    durationMs: t.durationMs ?? 4000,
    onClick:    t.onClick,
    createdAt:  Date.now(),
  };
  toastStore.toasts = [...toastStore.toasts, toast];
  // Auto-dismiss unless duration is 0 (sticky).
  if (toast.durationMs > 0) {
    setTimeout(() => dismiss(toast.id), toast.durationMs);
  }
  // Mirror to the OS notification surface if we're running under
  // Tauri. Web browsers only get the in-app toast.
  void platform.notify(toast.title, toast.body ?? '');
  return toast.id;
}

function dismiss(id: string): void {
  toastStore.toasts = toastStore.toasts.filter((t) => t.id !== id);
}

function clear(): void {
  toastStore.toasts = [];
}

export const toast = { push, dismiss, clear };
